
import ApprovalActions from "@/features/shared/components/sections/ApprovalActions.vue";
import RejectionDetails from "@/features/shared/components/RejectionDetails.vue";
import Component from "vue-class-component";
import Vue from "vue";
import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
import Files from "@/components/Files.vue";
import {
    factory,
    Gender,
    GetMedicalCertificateResponse,
    IMedicalCertificateActions,
    MedicalCertificateActions,
    MedicalCertificatesClient,
    MinimalDocumentModel
} from "@/api-client";
import SectionDocumentMetaData from "@/features/shared/components/sections/SectionDocumentMetaData.vue";
import MedicalCertificateDetailsContent from "@/features/medicalCertificates/components/MedicalCertificateDetailsContent.vue";
import ValidityStatusBadge from "@/features/shared/components/ValidityStatusBadge.vue";
import {Prop} from "vue-property-decorator";
import {NavigationGuardNext} from "vue-router/types/router";
import Handlebars from "handlebars";
import {formatDate} from "@/formatting/dateFormatter";
import {translateTrueFalseEng, translateTrueFalseEst} from "@/features/shared/TrueFalseTranslator";
import MinimalDocumentDetails from "@/features/documents/MinimalDocumentDetails.vue";
import DetailsLayout from "@/features/personProfile/components/DetailsLayout.vue";
import QrCode from "@/components/QrCode.vue";
import BaseDocumentApplicationUsageWidget from "@/features/documents/components/BaseDocumentApplicationUsageWidget.vue";
import ButtonWithConfirm from "@/features/shared/components/ButtonWithConfirm.vue";
import toasts from "@/components/Toasts";
import waitForAllImagesLoaded from "@/components/WaitForImagesLoaded";

const accessTokenStorageKey = 'mis-medical-certificate-access-token'

interface Props {
    preLoadedMedicalCertificate: GetMedicalCertificateResponse | MinimalDocumentModel;
    id: string;
    showMetadata: boolean;
}

@Component({
    components: {
        ApprovalActions,
        RejectionDetails,
        MedicalCertificateDetailsContent,
        SectionPageHeading,
        SectionDocumentMetaData,
        Files,
        ValidityStatusBadge,
        MinimalDocumentDetails,
        DetailsLayout,
        QrCode,
        BaseDocumentApplicationUsageWidget,
        ButtonWithConfirm
    },
    async beforeRouteEnter(to, from, next: NavigationGuardNext<MedicalCertificate>) {
        const props = to.meta!.props as Props;

        try {
            // This code is duplicated in MedicalCertificate.vue, CertificateDetails.vue and EndorsementDetails.vue.
            if (to.params.accessToken) {
                // Save token to session storage and replace location without token to prevent token from being preserved in history
                const urlWithoutToken = to.fullPath.replace(to.params.accessToken, "");
                sessionStorage.setItem(accessTokenStorageKey, to.params.accessToken as string);
                await window.location.replace(urlWithoutToken);
                return;
            }

            const accessToken = sessionStorage.getItem(accessTokenStorageKey);
            // Access token is not removed from session storage to enable language change and login when querying medical certificate by access token.

            if (!to.params.id && !accessToken) {
                await this.$router.push({ name: "not-found" });
                return;
            }

            // Either id OR access token is required
            const medicalCertificate = to.params.id
                ? await new MedicalCertificatesClient().get(to.params.id)
                : await new MedicalCertificatesClient().getByAccessToken(accessToken!);

            props.preLoadedMedicalCertificate = medicalCertificate;
            props.id = medicalCertificate.id;

            next();
        } catch (e) {
            next(e);
        }
    }
})
export default class MedicalCertificate extends Vue implements Props {
    @Prop({required: true})
    preLoadedMedicalCertificate!: GetMedicalCertificateResponse | MinimalDocumentModel;
    @Prop()
    id!: string;
    @Prop({default: true})
    showMetadata!: boolean;
    @Prop({default: true})
    addContainerCss!: boolean;

    @Prop({default: null})
    editRouteName?: string;

    medicalCertificate: GetMedicalCertificateResponse | MinimalDocumentModel = this.preLoadedMedicalCertificate;
    actions: IMedicalCertificateActions = factory.create(MedicalCertificateActions);

    $refs!: {
        qrCode: QrCode;
    }

    async created() {
        await this.updateAvailableActions();
    }

    async approve() {
        await new MedicalCertificatesClient().approve(this.medicalCertificate.id, (this.medicalCertificate as GetMedicalCertificateResponse).version!);
        this.medicalCertificate = await new MedicalCertificatesClient().get(this.medicalCertificate.id);
        await this.updateAvailableActions();
    }

    async disapprove(comment: string) {
        await new MedicalCertificatesClient().disapprove(this.medicalCertificate.id, comment);
        this.medicalCertificate = await new MedicalCertificatesClient().get(this.medicalCertificate.id);
        await this.updateAvailableActions();
    }

    async deleteCertificate() {
        await new MedicalCertificatesClient()
            .ignoreNotFound()
            .delete(this.medicalCertificate.id)
            .then(async () => {
                toasts.success(this.$ls.Documents_Toast_Success_DocumentDeletion);
            })
            // TODO(KK): Handle errors?

        if (this.preLoadedMedicalCertificate instanceof GetMedicalCertificateResponse)
            this.$router.push({name: "medical-certificates", params: { seafarerId: this.preLoadedMedicalCertificate.seafarer.id }});
        else {
            this.$router.push({name: "home"}); // Should never happen.
        }
    }

    async updateAvailableActions() {
        if (!this.areFullDetailsShown)
            return;

        this.actions = await new MedicalCertificatesClient().availableActions(this.id);
    }

    async getDownloadUrl(fileId: string): Promise<string> {
        return (await new MedicalCertificatesClient().downloadFile(this.$route.params.id, fileId)).url;
    }

    get areFullDetailsShown() {
        return this.medicalCertificate instanceof GetMedicalCertificateResponse;
    }

    navigateToEdit(){
        if (this.medicalCertificate instanceof GetMedicalCertificateResponse)
            this.$router.push({
               name: this.editRouteName ? this.editRouteName : 'medical-certificate-edit',
               params: { id: this.medicalCertificate.id, seafarerId: this.medicalCertificate.seafarer.id }
            });
    }

    async print() {
        // Make a new request because download may have expired at this point.
        const medicalCertificate = await new MedicalCertificatesClient().get(this.medicalCertificate.id);
        console.log(medicalCertificate.printTemplateDownloadUrl.url);
        const templateFile = await fetch(medicalCertificate.printTemplateDownloadUrl.url, {
            method: 'GET'
        });

        const template = Handlebars.compile(await templateFile.text());

        /*eslint-disable */
        const renderedTemplate = template({
            TOENDINR: medicalCertificate.number,
            EESNIMI: medicalCertificate.seafarer.firstName,
            PERENIMI: medicalCertificate.seafarer.lastName,
            SYNNIAEG: formatDate(medicalCertificate.seafarer.birthdate),
            KODAKONDSUS: medicalCertificate.seafarer.nationality?.estonian || "-",
            KODAKONDSUSENG: medicalCertificate.seafarer.nationality?.english || "-",
            SUGU: translateGender(medicalCertificate.seafarer.gender, false),
            SUGUENG: translateGender(medicalCertificate.seafarer.gender, true),
            VALJASTAMISEKPV: formatDate(medicalCertificate.issuedOn),
            KEHTIBKUNIKPV: formatDate(medicalCertificate.validUntil),
            ARSTIEESNIMI: medicalCertificate.medicalPractitionerFirstName,
            ARSTIPERENIMI: medicalCertificate.medicalPractitionerLastName,
            DOKUMENTIKONTROLLITI: translateTrueFalseEst(medicalCertificate.identificationDocumentsWereChecked),
            DOKUMENTIKONTROLLITIENG: translateTrueFalseEng(medicalCertificate.identificationDocumentsWereChecked),
            KUULMINEVASTABAI9: translateTrueFalseEst(medicalCertificate.hearing.meetsStandardsInSectionAi9),
            KUULMINEVASTABAI9ENG: translateTrueFalseEng(medicalCertificate.hearing.meetsStandardsInSectionAi9),
            ABIVAHENDITETAKUULMINE: translateTrueFalseEst(medicalCertificate.hearing.unaidedHearingIsSatisfactory),
            ABIVAHENDITETAKUULMINEENG: translateTrueFalseEng(medicalCertificate.hearing.unaidedHearingIsSatisfactory),
            NAGEMISTERAVUSVASTABAI9: translateTrueFalseEst(medicalCertificate.vision.visualAcuityMeetsStandardsInSectionAi9),
            NAGEMISTERAVUSVASTABAI9ENG: translateTrueFalseEng(medicalCertificate.vision.visualAcuityMeetsStandardsInSectionAi9),
            VARVUSNAGEMINEVASTABAI9: translateTrueFalseEst(medicalCertificate.vision.colorVisionMeetsStandardsInSectionAi9),
            VARVUSNAGEMINEVASTABAI9ENG: translateTrueFalseEng(medicalCertificate.vision.colorVisionMeetsStandardsInSectionAi9),
            VARVUSNAGEMISEKONTROLLIKPV: formatDate(medicalCertificate.vision.dateOfLastColorVisionTest),
            SOBIVNAVVAHIKSJAVAATLEJAKS: translateTrueFalseEst(medicalCertificate.verdict.isFitForNavigationalWatch),
            SOBIVNAVVAHIKSJAVAATLEJAKSENG: translateTrueFalseEng(medicalCertificate.verdict.isFitForNavigationalWatch),
            SOBIVTOOTAMALAEVAL: translateTrueFalseEst(medicalCertificate.verdict.isFitForServiceAtSea),
            SOBIVTOOTAMALAEVALENG: translateTrueFalseEng(medicalCertificate.verdict.isFitForServiceAtSea),
            AMETIKOHT: medicalCertificate.positionName?.estonian,
            AMETIKOHTENG: medicalCertificate.positionName?.english,
            TERVISESEISUNDVASTAB: translateTrueFalseEst(medicalCertificate.verdict.healthConditionIsFitForServiceAtSea),
            TERVISESEISUNDVASTABENG: translateTrueFalseEng(medicalCertificate.verdict.healthConditionIsFitForServiceAtSea),
            ONPIIRANGUD: translateTrueFalseEst(!!medicalCertificate.restrictionNames.length),
            ONPIIRANGUDENG: translateTrueFalseEng(!!medicalCertificate.restrictionNames.length),
            PIIRANGUD: medicalCertificate.restrictionNames.map(x => x.estonian),
            PIIRANGUDENG: medicalCertificate.restrictionNames.map(x => x.english),
            PRILLIDVOIKONTAKTLAATSED: translateTrueFalseEst(medicalCertificate.vision.spectaclesOrContactLensesRequired),
            PRILLIDVOIKONTAKTLAATSEDENG: translateTrueFalseEng(medicalCertificate.vision.spectaclesOrContactLensesRequired),
            VALJAANDJAASUTUSENG: medicalCertificate.medicalInstitution.name.english,
            VALJAANDJAREGISTRIKOOD: medicalCertificate.medicalInstitution.registerCode,
            VALJAANDJAAADRESS: medicalCertificate.medicalInstitution.address,
            VALJAANDJAASUTUS: medicalCertificate.medicalInstitution.name.estonian,
            VALJAANDJAEMAIL: medicalCertificate.medicalInstitution.email,
            VALJAANDJAPHONENUMBER: medicalCertificate.medicalInstitution.phoneNumber,
            MISAADRESS: window.location.host,
            UTN: medicalCertificate.accessToken,
            QRKOOD: this.$refs.qrCode.getBase64QrCode(),
            ARSTALLKIRI: medicalCertificate.currentMedicalPractitionerSignature,
            MEREMEESALLKIRI: medicalCertificate.currentSeafarerSignature
        })
        /*eslint-enable */

        const printWindow = window.open()!;
        printWindow.document.open();
        printWindow.document.write(renderedTemplate);
        printWindow.document.close();
        printWindow.focus();
        await waitForAllImagesLoaded(printWindow);
        printWindow.print();

        function translateGender(gender: Gender, inEnglish: boolean): string {
            switch (gender) {
                case Gender.Male:
                    return inEnglish ? "Male" : "Mees";
                case Gender.Female:
                    return inEnglish ? "Female" : "Naine";
                case Gender.Undefined:
                    return "";
            }
        }
    }
}
